import React, { useRef, useState } from 'react'
import { FBPage } from 'components/FbUI/Page/FBPage'
import {
  FBPageEyebrow,
  FBPageHeader,
  FBPageTableContainer,
  FBPageTitle,
  FBPageTitleRow,
} from 'components/FbUI/Page/styles'
import { WorkspaceOutlineIcon } from 'components/Layout/LeftNav/nav-icons'
import {
  ProductTable,
  type ProductTableHandle,
} from 'components/Tables/ProductTable'
import FbButton from 'components/FbUI/FbButton'
import { Plus } from 'lucide-react'
import { UnderlineTabs } from 'components/FbUI/UnderlineTabs'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { useProductsViewModeStore } from 'stores/productsViewModeStore'
import { useAuth } from 'context/authentication/useAuth'
import { Alert } from 'components/FbUI/Alert'

const productDescriptionText = `Current LTV Drivers: Cuisine, review composition, cross-platform ratings, menu dynamics, expense quartiles, chain size, demographics, population density, repeater rate, awareness effect, user inputs (churn, sales, etc). Allow up to an hour for LTV to calculate when updated.`

type ProductsTab = 'products' | 'variants'

export const ProductsPage: React.FC = () => {
  const { isLimited } = useAuth()
  const { viewMode, setViewMode } = useProductsViewModeStore()
  const tableRef = useRef<ProductTableHandle>(null)
  const [productsCount, setProductsCount] = useState<number | null>(null)
  const [variantsCount, setVariantsCount] = useState<number | null>(null)

  const handleCreateClick = () => {
    if (viewMode === 'products') {
      tableRef.current?.openCreateProductModal()
    } else {
      tableRef.current?.openCreateVariantModal()
    }
  }

  return (
    <FBPage
      innerPageClassName="!bg-[#fefefe]"
      containerClassName="!bg-[#fefefe] !shadow-none"
    >
      <FBPageHeader className="!mb-6">
        <FBPageTitleRow>
          <div className="flex flex-col items-start">
            <FBPageEyebrow>
              <WorkspaceOutlineIcon size={14} />
              Workspace
            </FBPageEyebrow>
            <FBPageTitle className="!text-fg">Products</FBPageTitle>
          </div>
        </FBPageTitleRow>
      </FBPageHeader>

      <Alert
        color="default"
        title="First Bite LTV Version 1.4"
        description={productDescriptionText}
        className="mb-3"
      />

      <div className="flex items-end justify-between gap-4 mb-3">
        <UnderlineTabs<ProductsTab>
          tabs={[
            { key: 'products', title: 'Products', count: productsCount },
            {
              key: 'variants',
              title: 'Product Variants',
              count: variantsCount,
            },
          ]}
          activeKey={viewMode}
          onChange={setViewMode}
          idPrefix="products-page"
          aria-label="Products and variants"
        />
        <FbButton
          disabled={isLimited}
          className="!bg-brand-bright !text-white hover:!bg-brand-active"
          onClick={handleCreateClick}
        >
          <Plus size={16} />
          {viewMode === 'products' ? 'Create Product' : 'Create Variant'}
        </FbButton>
      </div>

      <TableLinkStyleProvider variant="plain">
        <FBPageTableContainer
          className="flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]"
          style={{
            height: 'calc(var(--base-page-table-height) - 150px)',
          }}
        >
          <ProductTable
            ref={tableRef}
            tableKey="products-page-table"
            onProductsCountChange={setProductsCount}
            onVariantsCountChange={setVariantsCount}
          />
        </FBPageTableContainer>
      </TableLinkStyleProvider>
    </FBPage>
  )
}
